import React from "react";

import { SearchX } from "lucide-react";

import { CoverageChips, answerCoverage } from "./BriefingParts";
import { FLOW } from "./flowCopy";
import { Eyebrow } from "./PlanCard";
import { useAccountPlanFile } from "../../Hooks/useAccountPlan";

interface Props {
  accountId: string;
  currentFileId: string | null;
  /** A draft briefing exists for the current version. */
  hasDraft: boolean;
  onOpenQuestions: () => void;
}

const LINK =
  "rounded text-xs font-semibold text-[#8538d4] hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-[#974ae4]/50";

/** Mock v4 "Briefing Questions" coverage for the current plan version. */
const PlanCoverageCard: React.FC<Props> = ({
  accountId,
  currentFileId,
  hasDraft,
  onOpenQuestions,
}) => {
  const { file } = useAccountPlanFile(
    accountId,
    hasDraft ? currentFileId : null
  );
  const briefing = hasDraft ? file?.sponsor_view?.briefing : null;

  if (!briefing) {
    return (
      <section className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
        <Eyebrow>{FLOW.briefingQuestions}</Eyebrow>
        {hasDraft ? (
          <>
            <p className="mb-3 text-sm text-gray-500">
              {file?.sponsor_view?.briefing_status === "failed"
                ? "The Briefing Questions couldn't be answered for this draft."
                : "Not answered for this draft yet."}
            </p>
            <button type="button" onClick={onOpenQuestions} className={LINK}>
              Open Briefing Questions →
            </button>
          </>
        ) : (
          <p className="text-sm text-gray-500">
            Shown once the draft briefing is generated.
          </p>
        )}
      </section>
    );
  }

  const gaps = briefing.answers.filter(a => answerCoverage(a) !== "answered");
  // The plan number (D1); sponsor_view.file_version is the internal one.
  const version = file?.plan_version;
  return (
    <section
      data-testid="plan-coverage"
      className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm"
    >
      <Eyebrow>
        {FLOW.briefingQuestions}
        {version != null ? ` · plan v${version}` : ""}
      </Eyebrow>
      <div className="mb-3 flex flex-wrap gap-1.5">
        <CoverageChips coverage={briefing.coverage} />
      </div>
      {gaps.length > 0 && (
        <>
          <div className="mb-1.5 text-xs text-gray-500">
            Not fully answered by the plan — add to the next version:
          </div>
          <ul className="mb-3 space-y-1">
            {gaps.map(g => (
              <li
                key={g.question_id}
                className="flex gap-1.5 text-xs text-gray-700"
              >
                <SearchX
                  className="mt-0.5 h-3 w-3 shrink-0 text-amber-600"
                  aria-hidden
                />
                {g.question}
              </li>
            ))}
          </ul>
        </>
      )}
      <button type="button" onClick={onOpenQuestions} className={LINK}>
        Open Briefing Questions →
      </button>
    </section>
  );
};

export default PlanCoverageCard;
